import React, { useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FileText, Users, Camera, BarChart3, CreditCard, Settings, Clock, Shield, Download, Smartphone } from 'lucide-react';

const Features = () => {
  const { darkMode } = useContext(ThemeContext);

  const coreFeatures = [
    {
      icon: FileText,
      title: 'Invoice Management',
      description: 'Create, customize, and print professional invoices with ease.',
      details: [
        'Customizable invoice templates with your branding',
        'Automatic invoice numbering and tracking',
        'PDF generation for easy sharing and printing',
        'Payment status tracking and reminders',
        'Tax calculation and itemized billing',
        'Unlimited invoice creation'
      ]
    },
    {
      icon: Users,
      title: 'Customer Database',
      description: 'Comprehensive customer relationship management for your auto shop.',
      details: [
        'Store unlimited customer records',
        'Track complete service history per customer',
        'Vehicle ownership tracking',
        'Contact information and notes',
        'Quick customer search and filtering',
        'Customer communication logs'
      ]
    },
    {
      icon: Camera,
      title: 'Vehicle Management',
      description: 'Access detailed vehicle information and service records.',
      details: [
        'Comprehensive vehicle database with specifications',
        'VIN lookup and decoding',
        'Service history tracking per vehicle',
        'Maintenance schedule reminders',
        'FREE yearly database updates',
        'Parts and labor recommendations'
      ]
    },
    {
      icon: BarChart3,
      title: 'Reports & Analytics',
      description: 'Make data-driven decisions with powerful reporting tools.',
      details: [
        'Revenue and sales reports',
        'Customer analytics and trends',
        'Service type performance metrics',
        'Monthly, quarterly, and annual summaries',
        'Export reports to PDF and Excel',
        'Custom date range reporting'
      ]
    },
    {
      icon: CreditCard,
      title: 'Payment Processing',
      description: 'Secure and flexible payment options for your customers.',
      details: [
        'Multiple payment method support',
        'Partial payment tracking',
        'Payment history logs',
        'Receipt generation',
        'Outstanding balance tracking',
        'Payment reminder system'
      ]
    },
    {
      icon: Settings,
      title: 'Customizable Settings',
      description: 'Tailor the software to match your business needs.',
      details: [
        'Custom tax rates and labor rates',
        'Company branding and logo integration',
        'Invoice template customization',
        'User permissions and roles',
        'Business information management',
        'Automated backup settings'
      ]
    }
  ];

  const additionalFeatures = [
    {
      icon: Clock,
      title: 'Time Tracking',
      description: 'Track labor hours and improve efficiency'
    },
    {
      icon: Shield,
      title: 'Data Security',
      description: 'Your data is protected with industry-standard encryption'
    },
    {
      icon: Download,
      title: 'Easy Export',
      description: 'Export data to Excel, PDF, and other formats'
    },
    {
      icon: Smartphone,
      title: 'Responsive Design',
      description: 'Works seamlessly on desktop, tablet, and mobile'
    }
  ];

  return (
    <div className={`min-h-screen flex flex-col ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900`}>
      <Navbar />
      <div className="pt-16 px-4 sm:px-5 lg:px-7 flex-grow">
        <div className="max-w-[1440px] mx-auto py-10">
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Powerful Features for Your Auto Shop
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Everything you need to streamline your automotive business operations, all in one comprehensive solution.
            </p>
          </div>

          {/* Core Features - Detailed Cards */}
          <div className="space-y-8 mb-16">
            {coreFeatures.map((feature, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
                <div className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg flex-shrink-0">
                      <feature.icon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex-grow">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {feature.title}
                      </h2>
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        {feature.description}
                      </p>
                      <ul className="grid md:grid-cols-2 gap-3">
                        {feature.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span className="text-gray-700 dark:text-gray-300 text-sm">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Additional Features Grid */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Additional Benefits
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalFeatures.map((feature, index) => (
                <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
                  <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-800 dark:to-indigo-900 rounded-xl shadow-xl p-8 text-center text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl mb-6 text-blue-100">
              Start your free trial today and experience all these powerful features for yourself.
            </p>
            <a
              href="/register"
              className="inline-block bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
            >
              Start Free Trial
            </a>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Features;